import type { ChatMessage } from '@/lib/support-chat/types'
import { SupportMessageBody } from '@/lib/support/SupportMessageBody'
import { fileSize } from '../../../app/account/support/supportTypes'

export function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  document.body.append(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

function messageTime(value: string) {
  return new Date(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function ChatMessages({
  messages,
  browserContext,
}: {
  messages: ChatMessage[]
  browserContext: string
}) {
  return (
    <div
      className="flex flex-col gap-2"
      aria-label="Chat messages"
      role="log"
      aria-live="polite"
    >
      {messages.map(message => {
        const mine = message.authorName === 'You'
        return (
          <article
            key={message.id}
            className={`flex max-w-[85%] gap-2 ${mine ? 'flex-col items-end self-end' : 'items-end self-start'}`}
          >
            <p className="sr-only">{message.authorName}</p>
            {!mine && (
              <span
                className="mb-5 flex size-7 shrink-0 items-center justify-center rounded-full bg-vi-red text-[10px] font-semibold text-white"
                aria-hidden="true"
              >
                VI
              </span>
            )}
            <div className="flex min-w-0 flex-col gap-1">
            <div
              className={`rounded-2xl px-3.5 py-2.5 text-sm leading-5 ${
                mine
                  ? 'rounded-br-md bg-vi-red text-white'
                  : 'rounded-bl-md bg-gray-100 text-gray-900'
              }`}
            >
              <SupportMessageBody
                className={`break-words leading-5 [&_p]:mb-1.5 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_a]:underline ${
                  mine ? '[&_a]:text-white' : '[&_a]:text-vi-red'
                }`}
                html={message.html}
                filePath="/api/support-chat/downloads"
                browserContext={browserContext}
              />
              {message.attachments.length > 0 && (
                <ul
                  className={`mt-2 space-y-1 border-t pt-1.5 ${mine ? 'border-white/20' : 'border-gray-200'}`}
                  aria-label="Attachments"
                >
                  {message.attachments.map(file => (
                    <li key={file.id} className="break-all text-xs">
                      {file.state === 'Available' ? (
                        <a
                          href={`/api/support-chat/downloads/${file.id}?context=${encodeURIComponent(browserContext)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`underline ${mine ? 'text-white' : 'text-vi-red'}`}
                          aria-label={`Download ${file.name}`}
                        >
                          {file.name}
                        </a>
                      ) : (
                        <span>
                          {file.name} ·{' '}
                          {file.state === 'Processing'
                            ? 'Checking file…'
                            : 'File unavailable'}
                        </span>
                      )}
                      <span
                        className={`ml-1 ${mine ? 'text-white/70' : 'text-gray-500'}`}
                      >
                        ({fileSize(file.size)})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <time
              className={`px-1 text-[11px] text-gray-400 ${mine ? 'text-right' : ''}`}
              dateTime={message.createdAtUtc}
            >
              {messageTime(message.createdAtUtc)}
            </time>
            </div>
          </article>
        )
      })}
    </div>
  )
}
